import { AppLocale, ENABLED_APP_LOCALES, EnabledAppLocale } from "./localeRegistry";

export const APP_LOCALE_STORAGE_KEY = "cirrus_data_cloud_locale";

export interface LocaleResolution {
    locale: EnabledAppLocale;
    source: "stored_preference" | "browser" | "default";
}

export interface LocaleResolutionInput {
    storedPreference?: string | null;
    browserLanguages?: readonly string[];
}

export const canonicalizeAppLocale = (value: string | null | undefined): AppLocale | undefined => {
    if (!value) {
        return undefined;
    }

    const normalized = value.trim().replaceAll("_", "-");
    const lower = normalized.toLowerCase();

    if (lower === "en" || lower.startsWith("en-")) {
        return "en";
    }

    if (lower === "es" || lower.startsWith("es-")) {
        return "es";
    }

    if (lower === "fr" || lower.startsWith("fr-")) {
        return "fr";
    }

    if (
        lower === "zh-cn" ||
        lower.startsWith("zh-cn-") ||
        lower === "zh-sg" ||
        lower.startsWith("zh-sg-") ||
        lower === "zh-hans" ||
        lower.startsWith("zh-hans-")
    ) {
        return "zh-CN";
    }

    if (
        lower === "zh-tw" ||
        lower.startsWith("zh-tw-") ||
        lower === "zh-hk" ||
        lower.startsWith("zh-hk-") ||
        lower === "zh-mo" ||
        lower.startsWith("zh-mo-") ||
        lower === "zh-hant" ||
        lower.startsWith("zh-hant-")
    ) {
        return "zh-TW";
    }

    if (lower === "ko" || lower.startsWith("ko-")) {
        return "ko";
    }

    if (lower === "ja" || lower.startsWith("ja-")) {
        return "ja";
    }

    return undefined;
};

export const asEnabledAppLocale = (value: string | null | undefined): EnabledAppLocale | undefined => {
    const locale = canonicalizeAppLocale(value);
    return ENABLED_APP_LOCALES.find((enabledLocale) => enabledLocale === locale);
};

export const resolveAppLocale = (input: LocaleResolutionInput = {}): LocaleResolution => {
    const storedLocale = asEnabledAppLocale(input.storedPreference);
    if (storedLocale) {
        return { locale: storedLocale, source: "stored_preference" };
    }

    for (const browserLanguage of input.browserLanguages ?? []) {
        const browserLocale = asEnabledAppLocale(browserLanguage);
        if (browserLocale) {
            return { locale: browserLocale, source: "browser" };
        }
    }

    return { locale: "en", source: "default" };
};

export const getInitialLocaleResolution = (): LocaleResolution => {
    if (typeof window === "undefined") {
        return resolveAppLocale();
    }

    let storedPreference: string | null = null;
    try {
        storedPreference = window.localStorage.getItem(APP_LOCALE_STORAGE_KEY);
    } catch {
        storedPreference = null;
    }

    return resolveAppLocale({
        storedPreference,
        browserLanguages: typeof navigator === "undefined" ? [] : navigator.languages,
    });
};

export const persistAppLocale = (locale: EnabledAppLocale): void => {
    if (typeof window === "undefined") {
        return;
    }
    try {
        window.localStorage.setItem(APP_LOCALE_STORAGE_KEY, locale);
    } catch {
        // The active language still changes when storage is unavailable.
    }
};
